/**
 * JourneyTimeline — Chronological activity feed for a customer journey.
 * Displays all activities from Lead creation onward.
 * Every activity is sourced from authoritative canonical records.
 *
 * Also renders AI Insight suggestion cards (B6/B7) when provided.
 */
import { useState } from "react";
import {
  UserPlus, UserCheck, Mail, Phone, MessageCircle, Calendar,
  Star, CheckCircle2, AlertCircle, FileText, Layers, Zap,
  ArrowRight, Link, Clock, Activity, Building2, Target,
  GitBranch, ShieldCheck, AlertTriangle, Sparkles, ChevronDown,
  ChevronUp, Check, X, GitPullRequest,
} from "lucide-react";

const ACTIVITY_META = {
  LEAD_CREATED:           { icon: UserPlus,       color: "#0176D3", label: "Lead Created",           bg: "bg-blue-50" },
  LEAD_ASSIGNED:          { icon: UserCheck,      color: "#0176D3", label: "Lead Assigned",           bg: "bg-blue-50" },
  LEAD_QUALIFIED:         { icon: Star,           color: "#2E844A", label: "Lead Qualified",           bg: "bg-green-50" },
  LEAD_STATUS_CHANGED:    { icon: Activity,       color: "#706E6B", label: "Status Changed",           bg: "bg-gray-50" },
  LEAD_CONVERTED:         { icon: CheckCircle2,   color: "#2E844A", label: "Lead Converted",           bg: "bg-green-50" },
  ACCOUNT_LINKED:         { icon: Building2,      color: "#0176D3", label: "Account Linked",           bg: "bg-blue-50" },
  CONTACT_LINKED:         { icon: UserCheck,      color: "#0176D3", label: "Contact Linked",           bg: "bg-blue-50" },
  OPPORTUNITY_CREATED:    { icon: Target,         color: "#2E844A", label: "Opportunity Created",      bg: "bg-green-50" },
  CALL:                   { icon: Phone,          color: "#0176D3", label: "Call",                     bg: "bg-blue-50" },
  EMAIL_SENT:             { icon: Mail,           color: "#0176D3", label: "Email Sent",               bg: "bg-blue-50" },
  EMAIL_RECEIVED:         { icon: Mail,           color: "#2E844A", label: "Email Received",           bg: "bg-green-50" },
  WHATSAPP_SENT:          { icon: MessageCircle,  color: "#25D366", label: "WhatsApp Sent",            bg: "bg-green-50" },
  WHATSAPP_RECEIVED:      { icon: MessageCircle,  color: "#25D366", label: "WhatsApp Received",        bg: "bg-green-50" },
  CUSTOMER_MESSAGE:       { icon: MessageCircle,  color: "#25D366", label: "Customer Message",         bg: "bg-green-50" },
  INTERNAL_NOTE:          { icon: FileText,       color: "#706E6B", label: "Internal Note",            bg: "bg-gray-50" },
  MEETING:                { icon: Calendar,       color: "#0176D3", label: "Meeting",                  bg: "bg-blue-50" },
  FOLLOW_UP:              { icon: Clock,          color: "#FE9339", label: "Follow-up",                bg: "bg-amber-50" },
  TASK_CREATED:           { icon: CheckCircle2,   color: "#706E6B", label: "Task Created",             bg: "bg-gray-50" },
  TASK_COMPLETED:         { icon: CheckCircle2,   color: "#2E844A", label: "Task Completed",           bg: "bg-green-50" },
  NOTE_ADDED:             { icon: FileText,       color: "#706E6B", label: "Note Added",               bg: "bg-gray-50" },
  EVENT_PLANNING_STARTED: { icon: Zap,            color: "#0176D3", label: "Event Planning Started",   bg: "bg-blue-50" },
  EVENT_CREATED:          { icon: Calendar,       color: "#0176D3", label: "Event Created",            bg: "bg-blue-50" },
  BRIEF_CREATED:          { icon: FileText,       color: "#0176D3", label: "Brief Created",            bg: "bg-blue-50" },
  BLUEPRINT_GENERATED:    { icon: Layers,         color: "#9B59B6", label: "Blueprint Generated",      bg: "bg-purple-50" },
  BLUEPRINT_APPROVED:     { icon: ShieldCheck,    color: "#2E844A", label: "Blueprint Approved",       bg: "bg-green-50" },
  THREAD_CREATED:         { icon: GitBranch,      color: "#0176D3", label: "Thread Activated",         bg: "bg-blue-50" },
  EVENT_CHANGE_CREATED:   { icon: AlertTriangle,  color: "#FE9339", label: "Event Change",             bg: "bg-amber-50" },
  EVENT_CHANGE_APPROVED:  { icon: CheckCircle2,   color: "#2E844A", label: "Change Approved",          bg: "bg-green-50" },
  APPROVAL_REQUESTED:     { icon: ShieldCheck,    color: "#FE9339", label: "Approval Requested",       bg: "bg-amber-50" },
  APPROVAL_COMPLETED:     { icon: ShieldCheck,    color: "#2E844A", label: "Approval Completed",       bg: "bg-green-50" },
  DECISION_CREATED:       { icon: AlertCircle,    color: "#0176D3", label: "Decision Required",        bg: "bg-blue-50" },
  DECISION_RESOLVED:      { icon: CheckCircle2,   color: "#2E844A", label: "Decision Resolved",        bg: "bg-green-50" },
  ISSUE_OPENED:           { icon: AlertCircle,    color: "#BA0517", label: "Issue Opened",             bg: "bg-red-50" },
  ISSUE_RESOLVED:         { icon: CheckCircle2,   color: "#2E844A", label: "Issue Resolved",           bg: "bg-green-50" },
  MILESTONE_COMPLETED:    { icon: CheckCircle2,   color: "#2E844A", label: "Milestone Completed",      bg: "bg-green-50" },
  READINESS_CHANGED:      { icon: Activity,       color: "#706E6B", label: "Readiness Updated",        bg: "bg-gray-50" },
  SYSTEM_EVENT:           { icon: Activity,       color: "#706E6B", label: "System Event",             bg: "bg-gray-50" },
};

const SEVERITY_BADGE = {
  CRITICAL: "bg-red-100 text-red-700",
  HIGH:     "bg-amber-100 text-amber-700",
  MEDIUM:   "bg-blue-100 text-blue-700",
  LOW:      "bg-gray-100 text-gray-700",
};

const FILTER_GROUPS = [
  { label: "All",           value: null },
  { label: "Customer",      value: new Set(["CALL","MEETING","FOLLOW_UP","TASK_CREATED","TASK_COMPLETED","NOTE_ADDED"]) },
  { label: "Communication", value: new Set(["EMAIL_SENT","EMAIL_RECEIVED","WHATSAPP_SENT","WHATSAPP_RECEIVED","CUSTOMER_MESSAGE","INTERNAL_NOTE"]) },
  { label: "CRM",           value: new Set(["LEAD_CREATED","LEAD_ASSIGNED","LEAD_QUALIFIED","LEAD_CONVERTED","LEAD_STATUS_CHANGED","ACCOUNT_LINKED","CONTACT_LINKED","OPPORTUNITY_CREATED","EVENT_CREATED","EVENT_PLANNING_STARTED","SYSTEM_EVENT"]) },
  { label: "Event Ops",     value: new Set(["THREAD_CREATED","BRIEF_CREATED","BLUEPRINT_GENERATED","BLUEPRINT_APPROVED","EVENT_CHANGE_CREATED","EVENT_CHANGE_APPROVED","APPROVAL_REQUESTED","APPROVAL_COMPLETED","DECISION_CREATED","DECISION_RESOLVED","ISSUE_OPENED","ISSUE_RESOLVED","MILESTONE_COMPLETED"]) },
  { label: "System",        value: new Set(["READINESS_CHANGED"]) },
];

function fmtTime(isoStr) {
  if (!isoStr) return "";
  try { return new Date(isoStr).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }); }
  catch { return ""; }
}

function fmtDate(isoStr) {
  if (!isoStr) return "Unknown date";
  try {
    const d = new Date(isoStr);
    const today = new Date(); today.setHours(0,0,0,0);
    const dayTs = new Date(d); dayTs.setHours(0,0,0,0);
    const diffDays = Math.round((today - dayTs) / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}

function groupByDate(activities) {
  const groups = {};
  (activities || []).forEach((a) => {
    const k = fmtDate(a.occurred_at);
    if (!groups[k]) groups[k] = [];
    groups[k].push(a);
  });
  return Object.entries(groups);
}

function ActivityItem({ activity, onNavigate }) {
  const meta = ACTIVITY_META[activity.activity_type] || ACTIVITY_META.SYSTEM_EVENT;
  const Icon = meta.icon;
  const isInbound = activity.direction === "INBOUND";
  const isMessage = ["EMAIL_SENT","EMAIL_RECEIVED","WHATSAPP_SENT","WHATSAPP_RECEIVED","CUSTOMER_MESSAGE"].includes(activity.activity_type);

  return (
    <div
      data-testid={`timeline-activity-${activity.activity_id}`}
      className="flex gap-3"
    >
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${meta.bg}`}>
        <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
      </div>

      <div className={`flex-1 min-w-0 rounded-lg p-2.5 ${
        isMessage
          ? `border ${isInbound ? "bg-[#F9FAFB] border-[#E5E5E5]" : "bg-[#EEF4FF] border-[#D1E4F6]"}`
          : "bg-transparent px-0"
      }`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>
              {meta.label}
            </span>
            {activity.actor_name && (
              <span className="text-[10px] text-[#706E6B] ml-1.5">— {activity.actor_name}</span>
            )}
          </div>
          <span className="text-[10px] text-[#B0ADAB] flex-shrink-0">{fmtTime(activity.occurred_at)}</span>
        </div>

        {activity.title && (
          <p className="text-sm text-[#181818] mt-0.5 leading-snug">{activity.title}</p>
        )}
        {activity.summary && (
          <p className="text-xs text-[#444444] mt-1 leading-relaxed whitespace-pre-wrap">{activity.summary}</p>
        )}

        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {activity.severity && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${SEVERITY_BADGE[activity.severity] || SEVERITY_BADGE.LOW}`}>
              {activity.severity}
            </span>
          )}
          {activity.channel && !["CRM","SYSTEM","INTERNAL"].includes(activity.channel) && (
            <span className="text-[10px] text-[#706E6B] px-1.5 py-0.5 rounded bg-[#F3F3F3]">
              {activity.channel}
            </span>
          )}
          {activity.route && (
            <button
              onClick={() => onNavigate && onNavigate(activity.route)}
              className="text-[10px] text-[#0176D3] hover:underline flex items-center gap-0.5"
            >
              Open record <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── B6/B7: AI Suggestion Card ─────────────────────────────────────────────────

const SIGNAL_LABELS = {
  BUDGET: "Budget Mentioned",
  GUEST_COUNT: "Guest Count",
  EVENT_DATE: "Event Date",
  VENUE: "Venue",
  EVENT_TYPE: "Event Type",
};

const SIGNAL_COLORS = {
  BUDGET:      "bg-green-100 text-green-800 border-green-200",
  GUEST_COUNT: "bg-blue-100 text-blue-800 border-blue-200",
  EVENT_DATE:  "bg-purple-100 text-purple-800 border-purple-200",
  VENUE:       "bg-orange-100 text-orange-800 border-orange-200",
  EVENT_TYPE:  "bg-teal-100 text-teal-800 border-teal-200",
};

function ConfidencePip({ confidence }) {
  const pct = Math.round(confidence * 100);
  const color = pct >= 85 ? "bg-green-500" : pct >= 65 ? "bg-amber-500" : "bg-red-400";
  return (
    <div className="flex items-center gap-1" title={`${pct}% confidence`}>
      <div className="w-12 h-1 rounded-full bg-[#E5E5E5] overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-[#706E6B]">{pct}%</span>
    </div>
  );
}

function SuggestionCard({ suggestion, onAction }) {
  const [acting, setActing] = useState(null);
  const sigColor = SIGNAL_COLORS[suggestion.signal_type] || "bg-gray-100 text-gray-800 border-gray-200";
  const sigLabel = SIGNAL_LABELS[suggestion.signal_type] || suggestion.signal_type;
  const hasEvent = Boolean(suggestion.event_id);

  const handleAction = async (action) => {
    setActing(action);
    try {
      await onAction?.(suggestion.id, action, suggestion);
    } finally {
      setActing(null);
    }
  };

  return (
    <div
      className="bg-white border border-amber-200 rounded-lg p-2.5 shadow-sm"
      data-testid={`suggestion-card-${suggestion.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${sigColor}`}>
            {sigLabel}
          </span>
          <span className="text-[10px] text-[#706E6B]">
            via {suggestion.source === "LLM" ? "AI" : "pattern match"}
          </span>
          <ConfidencePip confidence={suggestion.confidence} />
        </div>
        <button
          data-testid={`suggestion-dismiss-${suggestion.id}`}
          onClick={() => handleAction("dismiss")}
          disabled={acting === "dismiss"}
          className="text-[#C9C9C9] hover:text-[#706E6B] flex-shrink-0 mt-0.5 disabled:opacity-40"
          title="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="mt-1.5">
        <p className="text-sm font-semibold text-[#181818]">
          {suggestion.detected_value}
        </p>
        {suggestion.current_crm_value && (
          <p className="text-[10px] text-[#706E6B] mt-0.5">
            Current CRM value: <span className="font-mono">{suggestion.current_crm_value}</span>
          </p>
        )}
        {suggestion.message_excerpt && (
          <p className="text-[10px] text-[#706E6B] mt-1 italic line-clamp-2 border-l-2 border-amber-300 pl-2">
            "{suggestion.message_excerpt.substring(0, 120)}"
          </p>
        )}
      </div>

      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <button
          data-testid={`suggestion-apply-${suggestion.id}`}
          onClick={() => handleAction("apply")}
          disabled={!!acting}
          className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded bg-[#0176D3] text-white hover:bg-[#005FB2] disabled:opacity-40 transition-colors"
        >
          {acting === "apply" ? (
            <Clock className="w-3 h-3 animate-spin" />
          ) : (
            <Check className="w-3 h-3" />
          )}
          Update CRM
        </button>

        {hasEvent && (
          <button
            data-testid={`suggestion-change-request-${suggestion.id}`}
            onClick={() => handleAction("create-change-request")}
            disabled={!!acting}
            className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded border border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-40 transition-colors"
          >
            {acting === "create-change-request" ? (
              <Clock className="w-3 h-3 animate-spin" />
            ) : (
              <GitPullRequest className="w-3 h-3" />
            )}
            Create Change Request
          </button>
        )}
      </div>
    </div>
  );
}


export function JourneyTimeline({ activities = [], loading = false, onNavigate, suggestions = [], onSuggestionAction }) {
  const [activeFilter, setActiveFilter] = useState(null);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);

  const filtered = activeFilter
    ? activities.filter((a) => activeFilter.has(a.activity_type))
    : activities;

  const grouped = groupByDate(filtered);

  return (
    <div className="flex flex-col h-full" data-testid="journey-timeline">
      {/* Filters */}
      <div className="flex gap-1.5 p-3 border-b border-[#E5E5E5] overflow-x-auto flex-shrink-0">
        {FILTER_GROUPS.map((fg) => (
          <button
            key={fg.label}
            data-testid={`timeline-filter-${fg.label.toLowerCase()}`}
            onClick={() => setActiveFilter(fg.value)}
            className={`px-2.5 py-1 rounded text-xs font-medium flex-shrink-0 transition-colors ${
              activeFilter === fg.value
                ? "bg-[#0176D3] text-white"
                : "bg-[#F3F3F3] text-[#444444] hover:bg-[#E5E5E5]"
            }`}
          >
            {fg.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-[#706E6B] self-center flex-shrink-0">
          {filtered.length} activities
        </span>
      </div>

      {/* AI Insights — B6/B7 suggestion cards */}
      {suggestions.length > 0 && (
        <div className="flex-shrink-0 border-b border-[#E5E5E5]" data-testid="ai-suggestions-section">
          <button
            onClick={() => setSuggestionsExpanded((v) => !v)}
            className="flex items-center gap-2 w-full px-3 py-2 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
            data-testid="ai-suggestions-toggle"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-800">AI Insights</span>
            <span className="text-[10px] font-bold bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full ml-1">
              {suggestions.length}
            </span>
            <span className="text-[10px] text-amber-600 ml-1">
              signals detected in conversation
            </span>
            <span className="ml-auto text-amber-600">
              {suggestionsExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </span>
          </button>

          {suggestionsExpanded && (
            <div className="px-3 py-2 space-y-2 bg-amber-50/50">
              {suggestions.map((s) => (
                <SuggestionCard key={s.id} suggestion={s} onAction={onSuggestionAction} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activity list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin w-6 h-6 border-2 border-[#0176D3] border-t-transparent rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-[#706E6B]">
            No activities found
          </div>
        ) : (
          grouped.map(([date, acts]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px bg-[#E5E5E5] flex-1" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-[#706E6B] px-2">
                  {date}
                </span>
                <div className="h-px bg-[#E5E5E5] flex-1" />
              </div>
              <div className="space-y-2">
                {acts.map((act, i) => (
                  <ActivityItem key={act.activity_id || i} activity={act} onNavigate={onNavigate} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default JourneyTimeline;
